import { useEffect } from "react";

import type { UseMutationResult } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { isPaidPlan } from "@app/analytics/account/Billing/utils";
import useBillingContext from "@app/analytics/hooks/billing/useBillingContext";
import useUpdateSessionMetadata, {
  SessionMetadataUpdateBody,
} from "@app/analytics/hooks/meetings/useUpdateSessionMetadata";
import useEditStore from "@app/analytics/MeetingReport/stores/edits";
import type {
  EditAccessLevel,
  MeetingReportTier,
  SessionMetadata,
} from "@app/analytics/MeetingReport/types";
import useReportShare from "@app/common/access/ShareReport/hooks/useReportShare";
import { canEdit, isMeetingOwner } from "@app/common/access/ShareReport/utils";
import useAuth from "@app/common/auth/useAuth";
import useFeatureAccess from "@app/common/hooks/useFeatureAccess";
import type {
  BillingSubscription,
  MeetingPlatform,
  SessionInfo,
} from "@app/common/types";
import * as SessionUtils from "@app/common/utils/sessionUtils";
import { optionalParseUTCDateTimeFromString } from "@app/utils/utils";

import useMeetingInfoQuery from "./useMeetingInfoQuery";

const getSessionTitle = (sessionInfo?: SessionInfo) =>
  sessionInfo?.title ?? "Untitled Meeting";

const getTierChipTooltip = (
  tier: MeetingReportTier,
  subscription: BillingSubscription,
  currentUserCanUpgradeForReport: boolean
) => {
  if (
    tier === "out_of_quota" &&
    subscription.settings.monthlyFullMeetingLimit
  ) {
    return `You used your ${
      subscription.settings.monthlyFullMeetingLimit
    } premium meetings.${
      currentUserCanUpgradeForReport ? " Upgrade to generate a report." : ""
    }`;
  }

  if (tier === "lite") {
    return "Read was not given recording permissions by the meeting host, so data is very limited.";
  }

  if (tier === "deleted") {
    return "Unprocessed report data is retained for 30 days, then automatically deleted.";
  }
};

interface UseSessionMetadata {
  data: SessionMetadata;
  isLoading: boolean;
  updateSessionMetadata: UseMutationResult<
    void,
    unknown,
    SessionMetadataUpdateBody,
    void
  >;
}

const useSessionMetadata = (
  { enabled }: { enabled: boolean } = { enabled: true }
): UseSessionMetadata => {
  const { sessionId } = useParams();
  const { user } = useAuth();

  const { canAccessCoaching } = useFeatureAccess();
  const { isLoading: meetingInfoIsLoading, data: meetingInfo } =
    useMeetingInfoQuery(sessionId, { enabled });
  const { subscription } = useBillingContext();

  const { updateSessionMetadata } = useUpdateSessionMetadata();

  const meetingStatus = meetingInfo?.state;

  const { accessListQuery, accessList } = useReportShare({
    sessionId: sessionId!,
    enabled,
  });

  const isLoading = meetingInfoIsLoading || accessListQuery.isLoading;

  const setEditAccessLevel = useEditStore((state) => state.setEditAccessLevel);

  const dataExpirationDate = optionalParseUTCDateTimeFromString(
    meetingInfo?.dataExpirationDate
  );

  const dataDeleted = SessionUtils.isDataDeleted(meetingInfo);
  const isRestrictedReport = SessionUtils.isRestricted(meetingInfo);
  const isNotetakerReport = SessionUtils.isNotetaker(meetingInfo);
  const isUploadedSession = SessionUtils.isUploadedSession(meetingInfo);
  const isOverMaxSessionLength =
    SessionUtils.isOverMaxSessionLength(meetingInfo);

  const tier = dataDeleted
    ? "deleted"
    : isRestrictedReport
      ? "out_of_quota"
      : isNotetakerReport
        ? "notetaker"
        : SessionUtils.isReadLite(meetingInfo)
          ? "lite"
          : isUploadedSession
            ? "uploaded"
            : meetingInfo
              ? "full"
              : "unknown";

  const currentUserCanUpgradeForReport =
    // user can't upgrade to receive the report if they're already on a paid plan
    !isPaidPlan(subscription.plan) &&
    // post-billing-based processing, only users with direct access to the session can
    // trigger processing by upgrading (users with only link access have to wait until
    // a participant upgrades)
    ((meetingInfo?.isUpsellPreview ?? false) ||
      // pre-billing-based processing, any user could upgrade to receive the report;
      // access to metrics was based solely on the billing plan of the requesting user
      (isRestrictedReport && SessionUtils.isProcessed(meetingInfo)));

  const recordingEnabled = meetingInfo?.recordingEnabled ?? true;
  const transcriptionEnabled = meetingInfo?.transcriptionEnabled ?? true;

  const shouldUpsellRecordingEntities = isRestrictedReport && recordingEnabled;
  const shouldUpsellTranscriptEntities =
    isRestrictedReport && transcriptionEnabled;

  const editAccessLevel: EditAccessLevel =
    !user || isRestrictedReport || tier === "lite"
      ? "none"
      : canEdit(accessList.currentUserAccessLevel)
        ? "full"
        : // even if the user doesn't have edit access, they can still
          // edit their personal/private notes
          "notes_only";

  useEffect(() => {
    setEditAccessLevel(editAccessLevel);
  }, [editAccessLevel]);

  const billingPlanAtTimeOfSession =
    SessionUtils.getSessionBillingPlanByBillingState(meetingInfo);

  return {
    data: {
      billingPlanAtTimeOfSession,
      currentUserCanUpgradeForReport,
      currentUserHasCoachingAccess: canAccessCoaching,
      currentUserIsMeetingOwner: isMeetingOwner(
        accessList.currentUserAccessLevel
      ),
      dataDeleted,
      dataExpirationDate,
      endTime: optionalParseUTCDateTimeFromString(meetingInfo?.endTime),
      highlightReelUrl: meetingInfo?.highlightReelUrl,
      isNotetakerReport,
      isOverMaxSessionLength,
      isProcessing: SessionUtils.isProcessing(meetingInfo),
      isProcessingTriggeredByUpgrade: Boolean(meetingInfo?.isBillingUpgraded),
      isRecurringMeeting: Boolean(meetingInfo?.recurringEventId),
      isRestrictedReport,
      isUnsupportedLanguage: SessionUtils.isUnsupportedLanguage(meetingInfo),
      isUploadedSession,
      meetingPlatform:
        (meetingInfo?.meetingPlatform as MeetingPlatform) ?? "unknown",
      metricsLevel: meetingInfo?.metricsLevel ?? "unknown",
      recordingEnabled,
      scheduledStartTime: optionalParseUTCDateTimeFromString(
        meetingInfo?.calendarEvent?.startTime
      ),
      sessionId: sessionId!,
      shouldUpsellRecordingEntities,
      shouldUpsellTranscriptEntities,
      startTime: optionalParseUTCDateTimeFromString(meetingInfo?.startTime),
      status: meetingStatus,
      tags: meetingInfo?.tags ?? [],
      thumbnailUrl: meetingInfo?.thumbnailUrl,
      tier,
      tierTooltip: getTierChipTooltip(
        tier,
        subscription,
        currentUserCanUpgradeForReport
      ),
      tierDescription: meetingInfo?.billingState ?? "unknown",
      title: getSessionTitle(meetingInfo),
      transcriptionEnabled,
      uploadFileType: meetingInfo?.uploadFileType,
    },
    updateSessionMetadata,
    isLoading,
  };
};

export default useSessionMetadata;
